/**
 * SpotlightTour — first-login walkthrough of the real dashboard.
 *
 * Dims the page, cuts a hole around one real element at a time and explains it.
 * Skippable, resumable (server-persisted step) and replayable via the
 * "passadc:start-tour" window event. Steps whose target isn't on the page
 * (Pro-only widgets, empty states, mobile-hidden nav) are skipped automatically.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/App";
import {
  Compass, Gauge, Sparkles, Target, BookOpen, Clock, MessageSquareQuote,
  ChevronLeft, ChevronRight, X, Play,
} from "lucide-react";

const CARD_WIDTH = 360;
const GAP = 14;

const STEPS = [
  {
    id: "welcome",
    center: true,
    icon: Compass,
    title: "Let me show you around",
    body: "A few quick stops, about a minute. By the end you'll know exactly where to learn, where to practise and how to tell if you're ready. You can skip anytime.",
  },
  {
    id: "readiness",
    targets: ['[data-testid="readiness-score"]'],
    icon: Gauge,
    title: "The only number that matters",
    body: "Your pass probability, weighted the way the ADC actually weights each subject. It moves with every question you answer, so you always know where you stand.",
  },
  {
    id: "briefing",
    targets: ['[data-testid="daily-briefing"]'],
    icon: Sparkles,
    title: "Told what to study today",
    body: "Every morning this reads your recent sessions and gives you one clear instruction — no more staring at a question bank wondering where to start.",
  },
  {
    id: "practice",
    targets: ['[data-testid="path-practice-card"]'],
    icon: Target,
    title: "Practise real exam MCQs",
    body: "Exam-style questions modelled on the ADC Part 1 blueprint, each with a worked explanation. Answer, see why, move on — this is where most of your marks come from.",
  },
  {
    id: "learn",
    targets: ['[data-testid="path-learn-card"]'],
    icon: BookOpen,
    title: "Learn the concept first",
    body: "Short, exam-focused notes by topic. When a question catches you out, come here, read the topic, then go back and beat it.",
  },
  {
    id: "mock",
    targets: ['[data-testid="path-mock-card"]'],
    icon: Clock,
    title: "Test yourself under exam conditions",
    body: "Timed, full-length and scored like the real thing. Sit one early to get the shock out of the way, then again closer to your date.",
  },
  {
    id: "tutor",
    targets: ['[data-testid="mentor-chat-toggle"]', '[data-testid="smart-tutor-upsell"]'],
    icon: MessageSquareQuote,
    title: "Stuck? Ask the Smart Tutor",
    body: "Your tutor sits here on every study page. Ask it to explain anything in plain English — it answers from the same material your exam is written from.",
  },
  {
    id: "start",
    targets: ['[data-testid="quick-practice-btn"]'],
    icon: Play,
    title: "Start with 10 questions",
    body: "That's the tour. Ten questions is enough to give your readiness score something real to work with — let's go.",
    cta: { label: "Start practising", to: "/practice" },
  },
];

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const SpotlightTour = ({ paused = false }) => {
  const navigate = useNavigate();
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [spot, setSpot] = useState({ mode: "center", rect: null });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const cardRef = useRef(null);
  const [cardHeight, setCardHeight] = useState(220);

  const step = STEPS[index];

  // ---- lifecycle ----------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await api.get("/tour/state");
        if (cancelled) return;
        if (s.should_show) {
          setIndex(Math.min(s.step_index || 0, STEPS.length - 1));
          setActive(true);
        }
      } catch {
        /* never block the dashboard on the tour */
      }
    })();
    const onStart = () => { setIndex(0); setActive(true); };
    window.addEventListener("passadc:start-tour", onStart);
    return () => { cancelled = true; window.removeEventListener("passadc:start-tour", onStart); };
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const running = active && !paused;

  // ---- target measuring ---------------------------------------------------
  const measure = useCallback(() => {
    if (!step) return;
    if (step.center) { setSpot({ mode: "center", rect: null }); return; }
    const el = step.targets.map((s) => document.querySelector(s)).find(Boolean);
    if (!el || !el.getClientRects().length) { setSpot({ mode: "missing", rect: null }); return; }
    const r = el.getBoundingClientRect();
    setSpot({ mode: "rect", rect: { top: r.top, left: r.left, width: r.width, height: r.height } });
  }, [step]);

  useEffect(() => {
    if (!running || !step) return;
    if (!step.center) {
      const el = step.targets.map((s) => document.querySelector(s)).find(Boolean);
      el?.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "center",
      });
    }
    const t1 = setTimeout(measure, 60);
    const t2 = setTimeout(measure, 420);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(t1); clearTimeout(t2);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [running, step, measure, isMobile]);

  useLayoutEffect(() => {
    if (cardRef.current) setCardHeight(cardRef.current.offsetHeight || 220);
  }, [index, running, isMobile]);

  // ---- navigation ---------------------------------------------------------
  const persist = (i) => {
    api.post("/tour/progress", { step_index: i, step_id: STEPS[i]?.id }).catch(() => {});
  };

  const goTo = useCallback((i) => {
    const clamped = Math.max(0, Math.min(i, STEPS.length - 1));
    setIndex(clamped);
    persist(clamped);
  }, []);

  const finish = useCallback((cta) => {
    setActive(false);
    api.post("/tour/complete").catch(() => {});
    if (cta?.to) navigate(cta.to);
  }, [navigate]);

  const skip = useCallback(() => {
    setActive(false);
    api.post("/tour/skip", { step_index: index, step_id: STEPS[index]?.id }).catch(() => {});
  }, [index]);

  const next = useCallback(() => {
    if (index >= STEPS.length - 1) finish(STEPS[index]?.cta);
    else goTo(index + 1);
  }, [index, finish, goTo]);

  // Auto-skip steps whose widget isn't on this page (Pro-only, empty states…)
  useEffect(() => {
    if (!running || spot.mode !== "missing") return;
    const t = setTimeout(() => {
      if (index >= STEPS.length - 1) finish();
      else setIndex((i) => i + 1);
    }, 150);
    return () => clearTimeout(t);
  }, [running, spot.mode, index, finish]);

  useEffect(() => {
    if (!running) return;
    const onKey = (e) => {
      if (e.key === "Escape") skip();
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") goTo(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running, skip, next, goTo, index]);

  if (!running || !step) return null;

  const smooth = !prefersReducedMotion();
  const r = spot.rect;
  const holeStyle = r
    ? {
        top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12,
        transition: smooth ? "top .35s cubic-bezier(.4,0,.2,1), left .35s cubic-bezier(.4,0,.2,1), width .35s, height .35s" : "none",
      }
    : { top: "50%", left: "50%", width: 0, height: 0 };

  let cardStyle;
  if (isMobile || !r) {
    cardStyle = null; // centred / bottom-sheet via classes
  } else {
    const spaceBelow = window.innerHeight - (r.top + r.height);
    const top = spaceBelow > cardHeight + GAP + 24
      ? r.top + r.height + GAP
      : Math.max(16, r.top - GAP - cardHeight);
    const left = Math.min(
      Math.max(16, r.left + r.width / 2 - CARD_WIDTH / 2),
      window.innerWidth - CARD_WIDTH - 16,
    );
    cardStyle = {
      top, left, width: CARD_WIDTH,
      transition: smooth ? "top .35s cubic-bezier(.4,0,.2,1), left .35s cubic-bezier(.4,0,.2,1)" : "none",
    };
  }

  const Icon = step.icon;
  const isLast = index === STEPS.length - 1;

  return (
    <div data-testid="spotlight-tour" className="fixed inset-0 z-[200]" role="dialog" aria-modal="true" aria-label="Product tour">
      {/* Dim everything except the spotlit element */}
      <div
        data-testid="spotlight-hole"
        className="absolute rounded-2xl ring-2 ring-primary pointer-events-none"
        style={{ ...holeStyle, boxShadow: "0 0 0 9999px rgba(8, 12, 10, 0.78)" }}
      />
      {/* Click-catcher so the underlying UI can't be clicked mid-tour */}
      <div className="absolute inset-0" onClick={next} />

      <div
        ref={cardRef}
        data-testid="spotlight-card"
        style={cardStyle || undefined}
        className={
          cardStyle
            ? "absolute bg-card border border-border rounded-2xl shadow-2xl p-5"
            : (isMobile
                ? "absolute left-4 right-4 bottom-4 bg-card border border-border rounded-2xl shadow-2xl p-5"
                : "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(420px,calc(100vw-2rem))] bg-card border border-border rounded-2xl shadow-2xl p-6")
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4.5 h-4.5 text-primary" style={{ width: 18, height: 18 }} />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Step {index + 1} of {STEPS.length}
            </p>
            <h3 className="text-base md:text-lg font-semibold leading-snug" data-testid="spotlight-title">{step.title}</h3>
          </div>
          <button
            onClick={skip}
            data-testid="spotlight-skip-btn"
            aria-label="Skip tour"
            className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <p className="text-sm text-muted-foreground mt-3" data-testid="spotlight-body">{step.body}</p>

        <div className="flex items-center gap-1.5 mt-4">
          {STEPS.map((s, i) => (
            <span
              key={s.id}
              className={`h-1 rounded-full transition-all ${i === index ? "w-5 bg-primary" : "w-1.5 bg-secondary"}`}
            />
          ))}
        </div>

        <div className="flex items-center gap-2 mt-4">
          <button
            onClick={skip}
            data-testid="spotlight-skip-link"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors mr-auto"
          >
            Skip tour
          </button>
          {index > 0 && (
            <button
              onClick={() => goTo(index - 1)}
              data-testid="spotlight-back-btn"
              className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-border text-sm hover:bg-secondary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
          )}
          <button
            onClick={next}
            data-testid="spotlight-next-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
          >
            {isLast ? (step.cta?.label || "Finish") : "Next"}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpotlightTour;
